"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import type { PhotoItemType, VideoItemType } from "./types";
import { MediaEnum } from "./enums";
import { isVideoItem } from "./helpers";

interface SlideViewModeProps {
  items: (VideoItemType | PhotoItemType)[];
  imageCount: number;
  onClose: () => void;
  showVideos: boolean;
}

interface PhotoWithPosition {
  photo: PhotoItemType;
  position: number;
  isNew: boolean;
  isExiting: boolean;
}

interface MediaWithPosition {
  media: PhotoItemType | VideoItemType;
  position: number;
  isNew: boolean;
  isExiting: boolean;
}

interface ContainerDimensions {
  width: number;
  height: number;
}

// Animation configuration constants
const ANIMATION_DURATION_MS = 600;
const ANIMATION_CLEANUP_DELAY_MS = 700;
const GAP_PX = 8;
const CONTAINER_PADDING_PX = 16;

export default function SlideViewMode({
  items,
  imageCount,
  onClose,
  showVideos,
}: SlideViewModeProps) {
  console.log("SlideViewMode RENDER - items:", items. length, "imageCount:", imageCount);
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState<ContainerDimensions>({
    width: 0,
    height: 0,
  });
  
  // Track which images have loaded
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  
  // Filter photos and optionally videos
  const mediaItems = items.filter((item) => {
    if (item.mediaType === MediaEnum.Photo) return true;
    if (showVideos && item.mediaType === MediaEnum.Video) return true;
    return false;
  });

  // Get the most recent N media items
  const currentMediaItems = mediaItems.slice(0, imageCount);
  
  const previousPhotosRef = useRef<(PhotoItemType | VideoItemType)[]>(currentMediaItems);
  const [displayedMedia, setDisplayedMedia] = useState<MediaWithPosition[]>(
    currentMediaItems.map((media, index) => ({ 
      media, 
      position: index, 
      isNew: false, 
      isExiting: false 
    }))
  );

  // Calculate grid configuration based on image count, media aspect ratio, and container aspect ratio
  const getGridConfig = (
    count: number,
    mediaAspectRatio: number,
    containerAspectRatio: number
  ) => {
    if (count <= 0 || count === 1) return { cols: 1, rows: 1 };
    
    // Calculate the optimal cell aspect ratio that fits best in container
    // and matches the media aspect ratio
    
    // For landscape media on landscape screen: prefer more columns
    // For portrait media on landscape screen: prefer more rows (fewer cols)
    // Balance based on both ratios to maximize space usage
    
    const sqrt = Math.sqrt(count);
    const ratio = mediaAspectRatio / containerAspectRatio;
    
    let cols, rows;
    if (ratio > 1) {
      // Media is wider relative to container - use more columns
      cols = Math.ceil(sqrt * Math.sqrt(ratio));
      rows = Math.ceil(count / cols);
    } else {
      // Media is taller relative to container - use more rows
      rows = Math.ceil(sqrt * Math.sqrt(1 / ratio));
      cols = Math.ceil(count / rows);
    }
    
    // Ensure grid dimensions are reasonable - neither dimension should exceed count
    // and we want at least 1 col/row
    cols = Math.max(1, Math.min(cols, count));
    rows = Math.max(1, Math.ceil(count / cols));
    
    return { cols, rows };
  };

  // Calculate the dominant aspect ratio of the media items
  const dominantMediaAspectRatio = useMemo(() => {
    const uploadedItems = currentMediaItems.filter(item => {
      if (isVideoItem(item)) return item.uploaded > 0;
      return item.uploaded;
    });
    
    if (uploadedItems.length === 0) {
      return 16 / 9; // Fallback
    }
    
    const aspectRatios = uploadedItems
      .filter(item => item.width > 0 && item.height > 0) // Validate positive non-zero dimensions
      .map(item => item.width / item.height);
    
    if (aspectRatios.length === 0) {
      return 16 / 9; // Fallback
    }
    
    // Calculate average aspect ratio
    const avgAspectRatio = aspectRatios.reduce((sum, ratio) => sum + ratio, 0) / aspectRatios.length;
    return avgAspectRatio;
  }, [currentMediaItems]);

  // Calculate container aspect ratio
  const containerAspectRatio = useMemo(() => {
    if (containerDimensions.width === 0 || containerDimensions.height === 0) {
      return 16 / 9; // Fallback
    }
    return containerDimensions.width / containerDimensions.height;
  }, [containerDimensions]);

  const gridConfig = getGridConfig(imageCount, dominantMediaAspectRatio, containerAspectRatio);

  // Calculate position for each photo based on grid config and container dimensions
  const calculatePosition = (
    index: number, 
    isNew: boolean, 
    isExiting: boolean
  ): { top: number; left: number; width: number; height: number } => {
    const { cols, rows } = gridConfig;
    
    if (containerDimensions.width === 0 || containerDimensions.height === 0 || cols === 0 || rows === 0) {
      return { top: 0, left: 0, width: 0, height: 0 };
    }
    
    const cellWidth = (containerDimensions.width - GAP_PX * (cols - 1)) / cols;
    const cellHeight = (containerDimensions.height - GAP_PX * (rows - 1)) / rows;
    
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    const left = col * (cellWidth + GAP_PX);
    let top = row * (cellHeight + GAP_PX);
    
    if (isNew) {
      top = -cellHeight - GAP_PX;
    }
    
    if (isExiting) {
      top = containerDimensions.height + GAP_PX;
    }
    
    return { 
      top, 
      left, 
      width: cellWidth, 
      height: cellHeight 
    };
  };

  // Observe container dimensions for position calculations
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      setContainerDimensions({
        width: container.clientWidth - CONTAINER_PADDING_PX,
        height: container.clientHeight - CONTAINER_PADDING_PX,
      });
    };

    updateDimensions();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateDimensions);
      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  // Enter fullscreen mode when component mounts
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (error) {
        console.error("Failed to enter fullscreen:", error);
      }
    };

    enterFullscreen();

    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  // Handle ESC key press and fullscreen exit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    const handleFullscreenChange = () => {
      //if (!document.fullscreenElement) {
      //  onClose();
      //}
    };

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [onClose]);

   // Sync displayedMedia with currentMediaItems - handle new items, removed items, and updates
  useEffect(() => {
    // Recalculate currentMediaItems inside useEffect to avoid stale closure
    const freshMediaItems = items.filter((item) => {
      if (item.mediaType === MediaEnum.Photo) return true;
      if (showVideos && item.mediaType === MediaEnum.Video) return true;
      return false;
    });
    const freshCurrentMediaItems = freshMediaItems.slice(0, imageCount);
    const currentIds = freshCurrentMediaItems.map((p) => p.id);
    
    setDisplayedMedia((prev) => {
      const prevIds = prev.map((item) => item.media.id);
      
      // Check if there are new media items
      const hasNewMedia = currentIds.some((id) => !prevIds.includes(id));
      
      // Check if items were removed (excluding exiting ones)
      const hasRemovedMedia = prevIds.some(
        (id) => !currentIds.includes(id) && !prev.find((p) => p.media.id === id)?.isExiting
      );
      
      // If structure changed, rebuild the list
      if (hasNewMedia || hasRemovedMedia || prev.length === 0) {
        return freshCurrentMediaItems.map((media, index) => ({
          media,
          position: index,
           isNew: false,
          isExiting: false,
        }));
      }
      
      // Otherwise just update media data (e.g., uploaded status changed)
      return prev.map((item) => {
        const updatedMedia = freshCurrentMediaItems.find((p) => p.id === item.media.id);
        if (updatedMedia) {
          return { ...item, media: updatedMedia };
        }
        return item;
      });
    });
    
    previousPhotosRef.current = freshCurrentMediaItems;
  }, [items, imageCount, showVideos]);

  const visibleMedia = displayedMedia;
  const hasValidDimensions = containerDimensions.width > 0 && containerDimensions.height > 0;
  
  // Calculate aspect ratio for placeholders (memoized to avoid recalculation on every render)
  // Priority 1: First uploaded item from displayed media
  // Priority 2: First uploaded item from all media
  // Fallback: 16:9
  const placeholderAspectRatio = useMemo(() => {
    // Try to find first uploaded item in displayed media
    const uploadedDisplayed = currentMediaItems.find(item => {
      if (isVideoItem(item)) return item.uploaded > 0;
      return item.uploaded;
    });
    if (uploadedDisplayed && uploadedDisplayed.width && uploadedDisplayed.height) {
      return uploadedDisplayed.width / uploadedDisplayed.height;
    }
    
    // Try to find first uploaded item in all media
    const uploadedAll = mediaItems.find(item => {
      if (isVideoItem(item)) return item.uploaded > 0;
      return item.uploaded;
    });
    if (uploadedAll && uploadedAll.width && uploadedAll.height) {
      return uploadedAll.width / uploadedAll.height;
    }
    
    // Fallback to 16:9
    return 16 / 9;
  }, [currentMediaItems, mediaItems]);
  
  return (
    <div
      className="fixed inset-0 bg-black z-[9999] flex items-center justify-center cursor-pointer"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="relative w-full h-full p-2"
      >
        {hasValidDimensions && visibleMedia.map((item) => {
          const { media, position, isNew, isExiting } = item;
          const isVideo = isVideoItem(media);
          const isProcessed = isVideo ? media.uploaded > 0 : media.uploaded;
          const isImageLoaded = loadedImages.has(media.id);
          const id = `slide_media_${media.id}`;
          
          // Calculate position for this media
          const { top, left, width, height } = calculatePosition(position, isNew, isExiting);

          return (
            <div
              key={media.id}
              className="absolute overflow-hidden"
              style={{
                top: `${top}px`,
                left: `${left}px`,
                width: `${width}px`,
                height: `${height}px`,
                transition: `all ${ANIMATION_DURATION_MS}ms ease-out`,
                opacity: isExiting ? 0 : 1,
              }}
            >
              {/* Placeholder image with aspect ratio - shows for unprocessed media */}
              {!isProcessed && (
                <div
                  className="skeleton absolute inset-0 flex items-center justify-center bg-[#dadcde] pointer-events-none select-none z-20"
                >
                  <div
                    style={{
                      width: width > height * placeholderAspectRatio ? `${height * placeholderAspectRatio}px` : '100%',
                      height: height > width / placeholderAspectRatio ? `${width / placeholderAspectRatio}px` : '100%',
                    }}
                  >  
                    <Image
                      src="/assets/no-picture2.png"
                      width={800}
                      height={450}
                      className="object-contain w-full h-full"
                      alt="placeholder"
                    />
                  </div>
                </div>
              )}
              
              {/* Skeleton loading effect - shows when processed but not loaded yet */}
              <div
                className={`skeleton bg-[#dadcde] rounded-none absolute inset-0 pointer-events-none select-none z-10 transition-opacity ${
                  isProcessed && isImageLoaded ? "opacity-0" : "opacity-100"
                }`}
              />
              
              {/* Actual media - render when processed */}
              {isProcessed && (
                <>
                  {isVideo ? (
                    <video
                      id={id}
                      src={media.url}
                      className="object-contain w-full h-full"
                      autoPlay
                      loop
                      muted
                      playsInline
                      aria-label={media.name || media.id}
                      onLoadedData={() => setLoadedImages((prev) => new Set(prev).add(media.id))}
                      onError={() => {
                        setLoadedImages((prev) => {
                          const newSet = new Set(prev);
                          newSet.delete(media.id);
                          return newSet;
                        });
                        
                        // Retry loading video after delay (similar to image retry logic)
                        setTimeout(() => {
                          const element = document.querySelector(`#${id}`) as HTMLVideoElement;
                          if (element) {
                            element.src = media.url || '';
                            element.load();
                          }
                        }, 1500);
                      }}
                    />
                  ) : (
                    <Image
                      id={id}
                      src={media.url}
                      alt={media.name || media.id}
                      width={media.width}
                      height={media.height}
                      className="object-contain w-full h-full"
                      priority={position < 6}
                      unoptimized
                      onLoad={() => setLoadedImages((prev) => new Set(prev).add(media.id))}
                      onError={() => {
                        setLoadedImages((prev) => {
                          const newSet = new Set(prev);
                          newSet.delete(media.id);
                          return newSet;
                        });
                        
                        setTimeout(() => {
                          const element = document.querySelector(`#${id}`) as HTMLImageElement;
                          if (element) {
                            element.src = media.url;
                          }
                        }, 1500);
                      }}
                    />
                  )}
                </>
              )}
              
              {/* Uploading label - bottom right corner */}
              {!isProcessed && (
                <div className="absolute z-[60] inset-0 flex justify-end items-end pointer-events-none">
                  <p className="bg-black/60 text-white py-[2px] px-2 text-xs mb-2 mr-2 font-semibold rounded">
                    {t("Gallery.uploadingInProgress")}
                  </p>
                </div>
              )}
            </div>
          );
        })}
        {!hasValidDimensions && (
          <div className="absolute inset-0 bg-white" />
        )}
      </div>
    </div>
  );
}